/**
 * TELA 02 — CATEGORIA
 * Abre-alas oficial, busca interna, cards com fotos e ações de produtos simples ou configuráveis.
 */

import { CATEGORIES, PRODUCTS } from '../data/menuData.js';
import { formatCurrency, formatPriceDisplay } from '../utils/formatters.js';

export function renderCategoryView(container, store) {
  const category = CATEGORIES.find(c => c.id === store.activeCategoryId) || CATEGORIES[0];
  
  // Se for jantinha, o fluxo correto é o jantinhaView
  if (category.id === 'jantinhas') {
    store.setView('jantinha');
    return;
  }

  const allCategoryProducts = PRODUCTS.filter(p => p.categoryId === category.id);
  const searchQuery = (store.categorySearchQuery || '').trim().toLowerCase();

  const filteredProducts = searchQuery
    ? allCategoryProducts.filter(p => 
        p.name.toLowerCase().includes(searchQuery) || 
        (p.description && p.description.toLowerCase().includes(searchQuery)) ||
        (p.subCategory && p.subCategory.toLowerCase().includes(searchQuery))
      )
    : allCategoryProducts;

  // Agrupamento por subcategoria se houver
  const subCategories = [];
  filteredProducts.forEach(p => {
    const sub = p.subCategory || 'Geral';
    if (!subCategories.includes(sub)) {
      subCategories.push(sub);
    }
  });

  container.innerHTML = `
    <!-- Barra de Navegação da Categoria -->
    <div class="category-view-header">
      <div class="category-nav-bar">
        <button id="btn-back-home" class="btn btn-secondary btn-sm" style="display: inline-flex; align-items: center; gap: 6px;">
          <span>←</span> <span>Início</span>
        </button>
        <span style="color: var(--text-muted); font-size: 0.85rem;">/</span>
        <span style="font-weight: 700; color: var(--accent-gold); font-size: 0.95rem;">${category.name}</span>
      </div>

      <!-- Abre-alas Oficial da Categoria -->
      <div class="category-banner-wrap">
        <img src="${category.banner}" alt="${category.name}" class="category-banner-img" />
        <div class="category-banner-overlay">
          <h1 style="font-size: 1.8rem; font-weight: 800; color: #fff;">${category.name}</h1>
          <p style="font-size: 0.9rem; color: #e5e7eb; max-width: 600px;">${category.tagline}</p>
        </div>
      </div>

      <!-- Barra de Busca na Categoria -->
      <div class="category-search-bar">
        <span class="category-search-icon">🔍</span>
        <input 
          type="text" 
          id="category-search-input" 
          class="category-search-input" 
          placeholder="Buscar em ${category.name} (ex: nome ou ingrediente)..." 
          value="${store.categorySearchQuery || ''}"
        />
      </div>
    </div>

    <!-- Lista de Produtos -->
    <div class="category-products-container">
      ${filteredProducts.length === 0 ? `
        <div style="text-align: center; padding: 48px 16px; background: var(--bg-surface); border-radius: var(--radius-lg); border: 1px dashed var(--border-subtle);">
          <p style="font-size: 1.1rem; font-weight: 600; color: var(--text-muted);">Nenhum item encontrado para "${store.categorySearchQuery}".</p>
          <button id="btn-clear-search" class="btn btn-outline btn-sm" style="margin-top: 12px;">Limpar busca</button>
        </div>
      ` : ''}

      ${subCategories.map(subName => {
        const subProducts = filteredProducts.filter(p => (p.subCategory || 'Geral') === subName);
        if (subProducts.length === 0) return '';

        return `
          <section style="margin-bottom: 28px;">
            ${subName !== 'Geral' ? `
              <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 14px; padding-bottom: 6px; border-bottom: 1px solid var(--border-subtle);">
                <h2 style="font-size: 1.15rem; font-weight: 700; color: var(--accent-gold-light);">${subName}</h2>
                <span class="badge badge-gold">${subProducts.length} itens</span>
              </div>
            ` : ''}

            <div class="products-grid">
              ${subProducts.map(product => {
                const isConfigurable = product.allowBurgerAddons || product.prices || product.isCalzone || product.supportsHalf || product.hasSauceChoice || product.pricePending;
                
                // Exibição do preço base
                let displayPrice = '';
                if (product.prices) {
                  displayPrice = `A partir de ${formatCurrency(product.prices.broto)}`;
                } else if (product.pricePending) {
                  displayPrice = formatPriceDisplay(null, true, product.priceLabel);
                } else if (product.price) {
                  displayPrice = formatCurrency(product.price);
                }

                return `
                  <article class="product-card" data-product-id="${product.id}">
                    <div class="product-card-header">
                      <div>
                        <h3 class="product-title">${product.name}</h3>
                        ${product.highlight ? `<span class="badge badge-fire" style="margin-top: 4px;">Destaque</span>` : ''}
                        ${product.hasPendingNote ? `<span class="badge badge-pending" style="margin-top: 4px;">Obs. de cardápio</span>` : ''}
                      </div>
                    </div>

                    <p class="product-description">${product.description || ''}</p>

                    <div class="product-price-row">
                      <div>
                        <span class="product-price ${product.pricePending ? 'product-price-pending' : ''}">
                          ${displayPrice}
                        </span>
                        ${product.supportsHalf ? `<span style="font-size: 0.72rem; color: var(--text-muted); display: block;">Opção de 1/2 porção (60%)</span>` : ''}
                      </div>

                      <button class="btn btn-sm ${isConfigurable ? 'btn-primary' : 'btn-secondary'} btn-product-action" data-product-id="${product.id}">
                        ${isConfigurable ? 'Personalizar' : 'Pedir +'}
                      </button>
                    </div>
                  </article>
                `;
              }).join('')}
            </div>
          </section>
        `;
      }).join('')}
    </div>
  `;

  // Event Listeners
  container.querySelector('#btn-back-home')?.addEventListener('click', () => {
    store.setView('home');
  });

  const searchInput = container.querySelector('#category-search-input');
  searchInput?.addEventListener('input', (e) => {
    store.setCategorySearch(e.target.value);
  });

  container.querySelector('#btn-clear-search')?.addEventListener('click', () => {
    store.setCategorySearch('');
  });

  container.querySelectorAll('.btn-product-action').forEach(btn => {
    btn.addEventListener('click', (e) => {
      const productId = e.currentTarget.getAttribute('data-product-id');
      const product = PRODUCTS.find(p => p.id === productId);
      if (product) {
        store.setView('product', { product });
      }
    });
  });
}
